import { useState } from "react";
import "../../style/scss/FormInput.scss";
import "../../style/scss/Login.scss";
import UserData from "./UserData";
import { Form, FormInput, FormActions } from "../Controls/Form";
import API from "../../API";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomButton from "./CustomButton";
import { useModalBusy } from "./modalBusy";

export default function LoginForm({
  updateLogin,
  embedded = false,
  onClose,
  onSwitchToRegister,
}) {
  const [data, updateData] = useState({ email: "", password: "" });
  const [forgotStep, setForgotStep] = useState(null); // null | channel | sms | emailSent | setPassword
  const [smsCode, setSmsCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const { busy, withBusy } = useModalBusy();
  const navigate = useNavigate();

  async function onDoneAsync(e) {
    e.preventDefault();
    data.passwordConfirm = data.password;
    try {
      const resp = await API.postAsync("/Login", data);

      if (resp && resp.ok) {
        if (resp?.jsonData?.bearerToken)
          window.sessionStorage.setItem("BearerToken", resp.jsonData.bearerToken);
        if (resp?.jsonData?.userId)
          window.sessionStorage.setItem("UserId", resp.jsonData.userId);
        if (resp?.jsonData?.sessionId)
          window.sessionStorage.setItem("SessionId", resp.jsonData.sessionId);
        const mustChange =
          resp?.jsonData?.mustChangePassword === true ||
          resp?.jsonData?.MustChangePassword === true;
        if (mustChange) window.sessionStorage.setItem("MustChangePassword", "1");
        else window.sessionStorage.removeItem("MustChangePassword");
        await UserData.GetUserDataAsync();
        await UserData.GetLookupsAsync();
        updateLogin(true);
        onClose?.();
        navigate("/");
      } else {
        console.error("🔴 Login failed - full response:", resp);
        console.error("🔴 Login failed - response status:", resp?.status);
        console.error("🔴 Login failed - response statusText:", resp?.statusText);
        console.error("🔴 Login failed - response jsonData:", resp?.jsonData);
      }
    } catch (error) {
      console.error("🔴 Exception during login:", error);
    }
  }

  async function onChange(value, field) {
    if (value && typeof value === "object") {
      console.error("LoginForm onChange received an object for value. This should be a string.");
      return;
    }
    if (field && typeof field === "object") {
      console.error("LoginForm onChange received an object for field. This should be a string.");
      return;
    }
    updateData((prev) => ({ ...prev, [field]: value }));
  }

  function goToRegister(e) {
    e?.preventDefault?.();
    if (onSwitchToRegister) {
      onSwitchToRegister();
      return;
    }
    navigate("/register");
  }

  function resetForgotState() {
    setForgotStep(null);
    setSmsCode("");
    setResetToken("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setForgotMessage("");
    setForgotError("");
  }

  async function startEmailReset() {
    setForgotError("");
    try {
      const resp = await API.postAsync("/PasswordReset/Start", {
        email: data.email,
        channel: "email",
      });
      if (resp?.ok) {
        setForgotMessage(
          resp?.jsonData?.message ||
            "If an account exists for that email, a password reset link was sent."
        );
        setForgotStep("emailSent");
      } else {
        setForgotError(resp?.jsonData?.message || "Could not start password reset");
      }
    } catch (err) {
      setForgotError(err?.message || "Could not start password reset");
    }
  }

  async function onForgotPassword(e) {
    e?.preventDefault?.();
    const email = (data.email || "").trim();
    if (!email) {
      toast.error("Enter your email address first");
      return;
    }
    await withBusy(async () => {
      setForgotError("");
      setForgotMessage("");
      try {
        const resp = await API.postAsync("/PasswordReset/Channels", { email });
        if (!resp?.ok) {
          setForgotError(resp?.jsonData?.message || "Could not check reset options");
          return;
        }
        const smsAvailable = !!(resp.jsonData?.smsAvailable ?? resp.jsonData?.SmsAvailable);
        if (smsAvailable) {
          setForgotStep("channel");
        } else {
          await startEmailReset();
        }
      } catch (err) {
        setForgotError(err?.message || "Could not start password reset");
      }
    });
  }

  async function onChooseChannel(channel) {
    await withBusy(async () => {
      if (channel === "email") {
        await startEmailReset();
        return;
      }
      setForgotError("");
      try {
        const resp = await API.postAsync("/PasswordReset/Start", {
          email: data.email,
          channel: "sms",
        });
        if (resp?.ok) {
          setForgotMessage(
            resp?.jsonData?.message ||
              "If an account exists for that email, a text message was sent with a reset code."
          );
          setForgotStep("sms");
        } else {
          setForgotError(resp?.jsonData?.message || "Could not send reset code");
        }
      } catch (err) {
        setForgotError(err?.message || "Could not send reset code");
      }
    });
  }

  async function onVerifySms(e) {
    e?.preventDefault?.();
    await withBusy(async () => {
      setForgotError("");
      try {
        const resp = await API.postAsync("/PasswordReset/VerifySms", {
          email: data.email,
          code: smsCode,
        });
        if (resp?.ok && (resp.jsonData?.resetToken || resp.jsonData?.ResetToken)) {
          setResetToken(resp.jsonData.resetToken || resp.jsonData.ResetToken);
          setForgotStep("setPassword");
        } else {
          setForgotError(resp?.jsonData?.message || "Invalid or expired code");
        }
      } catch (err) {
        setForgotError(err?.message || "Invalid or expired code");
      }
    });
  }

  async function onCompleteReset(e) {
    e?.preventDefault?.();
    if (!newPassword || !newPasswordConfirm) {
      setForgotError("Enter and confirm your new password.");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setForgotError("New password and confirmation do not match.");
      return;
    }
    await withBusy(async () => {
      setForgotError("");
      try {
        const resp = await API.postAsync("/PasswordReset/Complete", {
          token: resetToken,
          password: newPassword,
          passwordConfirm: newPasswordConfirm,
        });
        if (resp?.ok) {
          toast.success("Password updated. You can sign in now.");
          resetForgotState();
        } else {
          setForgotError(resp?.jsonData?.message || "Unable to reset password");
        }
      } catch (err) {
        setForgotError(err?.message || "Unable to reset password");
      }
    });
  }

  if (forgotStep) {
    return (
      <div className={embedded ? "loginPage loginPage--embedded" : "loginPage"} data-testid="forgot-password-flow">
        <div className="login loginForm">
          <h2 style={{ marginTop: 0 }}>Forgot password</h2>
          {forgotStep === "channel" ? (
            <>
              <p className="fieldHint">How should we verify your identity?</p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <CustomButton
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={busy}
                  onClick={() => onChooseChannel("email")}
                  data-testid="forgot-channel-email"
                >
                  Email
                </CustomButton>
                <CustomButton
                  type="button"
                  variant="outlined"
                  color="secondary"
                  disabled={busy}
                  onClick={() => onChooseChannel("sms")}
                  data-testid="forgot-channel-sms"
                >
                  Text message
                </CustomButton>
              </div>
            </>
          ) : null}
          {forgotStep === "emailSent" ? (
            <p className="fieldHint" data-testid="forgot-email-sent">
              {forgotMessage || "Check your email for a reset link."}
            </p>
          ) : null}
          {forgotStep === "sms" ? (
            <form onSubmit={onVerifySms}>
              <p className="fieldHint">{forgotMessage}</p>
              <div className="field form-group">
                <label className="form-label" htmlFor="sms-reset-code">
                  Reset code
                </label>
                <input
                  id="sms-reset-code"
                  className="form-input"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  data-testid="forgot-sms-code"
                />
              </div>
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy}>
                Verify code
              </CustomButton>
            </form>
          ) : null}
          {forgotStep === "setPassword" ? (
            <form onSubmit={onCompleteReset}>
              <div className="field form-group">
                <label className="form-label" htmlFor="forgot-new-password">
                  New password
                </label>
                <input
                  id="forgot-new-password"
                  type="password"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="field form-group">
                <label className="form-label" htmlFor="forgot-new-password-confirm">
                  Confirm new password
                </label>
                <input
                  id="forgot-new-password-confirm"
                  type="password"
                  className="form-input"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy}>
                Set password
              </CustomButton>
            </form>
          ) : null}
          {forgotError ? <p className="fieldHint" style={{ color: "#b00020" }}>{forgotError}</p> : null}
          <p style={{ marginTop: "1rem" }}>
            <button type="button" className="mkt-text-link" onClick={resetForgotState} data-testid="forgot-back-to-login">
              Back to login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "loginPage loginPage--embedded" : "loginPage"}>
      <Form
        className="login loginForm"
        object={data}
        onChange={onChange}
        onUpdate={updateData}
      >
        <FormInput
          type="email"
          placeholder="Email Address"
          field="email"
          required
          autoFocus
          autoComplete="email"
          disablePwManager={true}
        />
        <FormInput
          type="password"
          placeholder="Password"
          field="password"
          required
          autoComplete="current-password"
          disablePwManager={true}
        />
        <p className="textCentered" style={{ margin: "0 0 0.75rem" }}>
          <button
            type="button"
            className="mkt-text-link"
            onClick={onForgotPassword}
            disabled={busy}
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </p>
        <FormActions onSubmitAsync={onDoneAsync} submitLabel="Login">
          {embedded ? (
            <p className="mkt-auth-switch">
              New here?{" "}
              {onSwitchToRegister ? (
                <button type="button" className="mkt-text-link" onClick={goToRegister}>
                  Create an account
                </button>
              ) : (
                <Link to="/register" onClick={goToRegister}>
                  Create an account
                </Link>
              )}
            </p>
          ) : null}
        </FormActions>
      </Form>
      {!embedded ? (
        <p className="centered">
          New here?{" "}
          <Link to="/register" onClick={goToRegister}>
            Create an account
          </Link>
        </p>
      ) : null}
    </div>
  );
}
